import { Box, FormControl, FormLabel, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { CabModal } from "@CabComponents/CabModal";
import { CabButton } from "@CabComponents/CabButton";
import { CabDatePicker } from "@CabComponents/CabDatePicker";
import { CabDropdown } from "@CabComponents/CabDropdown";
import { ContactCadence, ContactCadenceRequest } from "../../store/cabinetApi/generated/crm";
import { DateTime } from "luxon";
import { CabTextInput } from "@CabComponents/CabTextInput";

interface CadenceEditModalProps {
  open: boolean;
  onClose: () => void;
  cadence: ContactCadence | null;
  contactId: number;
  teammateId: number;
  onSaveCadence: (cadence: ContactCadenceRequest) => Promise<void>;
  isSaving?: boolean;
}

const frequencyTypeOptions = [
  { value: 'fixed_duration', label: 'Fixed Duration' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const advanceNoticeDurationOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const meetingFrequencyDurationOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];


export const CadenceEditModal = ({
  open,
  onClose,
  cadence,
  contactId,
  teammateId,
  onSaveCadence,
  isSaving = false
}: CadenceEditModalProps) => {
  const [startDate, setStartDate] = useState<string>('');
  const [frequencyType, setFrequencyType] = useState<string>('fixed_duration');
  const [meetingFrequencyAmount, setMeetingFrequencyAmount] = useState<number>(1);
  const [meetingFrequencyDuration, setMeetingFrequencyDuration] = useState<string>('weeks');
  const [advanceNoticeAmount, setAdvanceNoticeAmount] = useState<number>(1);
  const [advanceNoticeDuration, setAdvanceNoticeDuration] = useState<string>('days');

  useEffect(() => {
    if (cadence) {
      setStartDate(cadence.start_date);
      setFrequencyType(cadence.frequency_type);
      
      // Parse existing meeting frequency if it exists
      if (cadence.meeting_frequency) {
        const parsed = parseDurationFormat(cadence.meeting_frequency);
        setMeetingFrequencyAmount(parsed.amount);
        setMeetingFrequencyDuration(parsed.duration);
      } else {
        setMeetingFrequencyAmount(1);
        setMeetingFrequencyDuration('weeks');
      }
      
      // Parse existing advance notice if it exists
      if (cadence.advance_notice) {
        const parsed = parseDurationFormat(cadence.advance_notice);
        setAdvanceNoticeAmount(parsed.amount);
        setAdvanceNoticeDuration(parsed.duration);
      } else {
        setAdvanceNoticeAmount(1);
        setAdvanceNoticeDuration('days');
      }
    } else {
      // Set defaults for new cadence
      setStartDate(DateTime.now().toISODate() || '');
      setFrequencyType('fixed_duration');
      setMeetingFrequencyAmount(1);
      setMeetingFrequencyDuration('weeks');
      setAdvanceNoticeAmount(1);
      setAdvanceNoticeDuration('days');
    }
  }, [cadence, open]);

  const convertToDurationFormat = (amount: number, duration: string): string => {
    switch (duration) {
      case 'days':
        return `${amount} 00:00:00`;
      case 'weeks':
        return `${amount * 7} 00:00:00`;
      case 'months':
        return `${amount * 30} 00:00:00`;
      case 'years':
        return `${amount * 365} 00:00:00`;
      default:
        return `${amount} 00:00:00`;
    }
  };

  const parseDurationFormat = (durationString: string): { amount: number; duration: string } => {
    // Handle duration format like "7 00:00:00" or "30 00:00:00"
    const parts = durationString.split(' ');
    if (parts.length >= 1) {
      const days = parseInt(parts[0]);
      if (!isNaN(days)) {
        if (days % 365 === 0 && days >= 365) {
          return { amount: days / 365, duration: 'years' };
        } else if (days % 30 === 0 && days >= 30) {
          return { amount: days / 30, duration: 'months' };
        } else if (days % 7 === 0 && days >= 7) {
          return { amount: days / 7, duration: 'weeks' };
        } else {
          return { amount: days, duration: 'days' };
        }
      }
    }
    return { amount: 1, duration: 'days' };
  };

  const handleSave = async () => {
    if (!startDate || !frequencyType) return;
    
    // Meeting frequency is required for fixed_duration type
    if (frequencyType === 'fixed_duration' && meetingFrequencyAmount <= 0) {
      return;
    }
    
    // Advance notice is now required
    if (advanceNoticeAmount <= 0) {
      return;
    }

    const advanceNoticeString = convertToDurationFormat(advanceNoticeAmount, advanceNoticeDuration);
    const meetingFrequencyString = frequencyType === 'fixed_duration' 
      ? convertToDurationFormat(meetingFrequencyAmount, meetingFrequencyDuration)
      : null;
    
    const cadenceData: ContactCadenceRequest = {
      start_date: startDate,
      frequency_type: frequencyType as "fixed_duration" | "weekly" | "bi_weekly" | "monthly" | "quarterly" | "annually",
      meeting_frequency: meetingFrequencyString,
      advance_notice: advanceNoticeString,
      contact: contactId,
      teammate: teammateId,
    };

    // Add the cadence ID if we're updating an existing cadence
    if (cadence?.id) {
      (cadenceData as ContactCadenceRequest & { id: number }).id = cadence.id;
    }

    await onSaveCadence(cadenceData);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid = startDate && frequencyType && advanceNoticeAmount > 0 &&
    (frequencyType !== 'fixed_duration' || meetingFrequencyAmount > 0);

  return (
    <CabModal
      open={open}
      onClose={handleClose}
      title={cadence ? 'Edit Cadence Reminder' : 'Create Cadence Reminder'}
      closeIcon
      actionButtons={
        <>
          <CabButton buttonType="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </CabButton>
          <CabButton onClick={handleSave} disabled={!isFormValid || isSaving}>
            {isSaving ? 'Saving...' : cadence ? 'Update' : 'Create'}
          </CabButton>
        </>
      }
      sx={{ '& .MuiDialog-paper': { width: 500, maxHeight: '80%' } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          paddingTop: 2
        }}>
        <FormControl fullWidth>
          <FormLabel>Start Date</FormLabel>
          <CabDatePicker
            value={startDate}
            onChange={(date) => setStartDate(date || '')}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>Frequency Type</FormLabel>
          <CabDropdown
            value={frequencyType}
            onChange={(e) => setFrequencyType(e.target.value)}
            options={frequencyTypeOptions}
            placeholder="Select frequency type"
          />
        </FormControl>

        {frequencyType === 'fixed_duration' && (
          <Box
            sx={{
              display: "flex",
              gap: 2
            }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Meeting Frequency *</FormLabel>
              <CabTextInput
                type="number"
                value={meetingFrequencyAmount}
                onChange={(e) => setMeetingFrequencyAmount(parseInt(e.target.value) || 1)}
                placeholder="1"
              />
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Duration</FormLabel>
              <CabDropdown
                value={meetingFrequencyDuration}
                onChange={(e) => setMeetingFrequencyDuration(e.target.value)}
                options={meetingFrequencyDurationOptions}
                placeholder="Select duration"
              />
            </FormControl>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2
          }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Notification Reminder *</FormLabel>
            <CabTextInput
              type="number"
              value={advanceNoticeAmount}
              onChange={(e) => setAdvanceNoticeAmount(parseInt(e.target.value) || 1)}
              placeholder="1"
            />
          </FormControl>

          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Duration</FormLabel>
            <CabDropdown
              value={advanceNoticeDuration}
              onChange={(e) => setAdvanceNoticeDuration(e.target.value)}
              options={advanceNoticeDurationOptions}
              placeholder="Select duration"
            />
          </FormControl>
        </Box>

        {startDate && frequencyType && (
          <Box
            sx={{
              padding: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1
            }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              <strong>Preview:</strong>&nbsp;
              {frequencyType !== 'fixed_duration'
                ? frequencyType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' '
                : ''}
              Starting{' '} {DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_MED)}
              {frequencyType === 'fixed_duration' && meetingFrequencyAmount > 0 && 
                ` - Every ${meetingFrequencyAmount} ${meetingFrequencyDuration}`}
              {advanceNoticeAmount > 0 && ` (${advanceNoticeAmount} ${advanceNoticeDuration} notice)`}
            </Typography>
          </Box>
        )}
      </Box>
    </CabModal>
  );
};

export default CadenceEditModal;